/**
 * THE money formatters — one module, two pinned output styles, one rule.
 *
 * THE RULE: no default currency. Every amount this product prints travels with the
 * currency it is held in (a catalogue row's, a payment's, the platform's settlement
 * currency for a row naming none — resolved by the SERVER, `platform_helpers.
 * catalogue_currency`). For years a dozen local copies of these functions fell back to
 * `"cad"` / `"CAD"` when the currency was missing, which stopped being true the moment the
 * platform settled in USD: a missing currency then rendered as the RETIRED one. A bare
 * number is honest; an invented code is not.
 *
 * THREE STYLES, each pinned by tests and used on purpose:
 *
 *   formatMoneyCode(cents, currency, decimals)  → "108.00 CAD" / "90 USD"
 *     Locale-invariant, ISO code after the number. The admin plane's tables, the
 *     storefront cards, receipts-adjacent screens — anywhere the same string must read
 *     identically in EN and FR (ConsultRoomPage.locale.test.js pins that).
 *
 *   formatMoneyIntl(cents, currency, lang)      → "$45.00" (en-CA) / "45,00 $" (fr-CA)
 *     `Intl.NumberFormat`, locale from the UI language, currency SYMBOL style. The
 *     booking lane (Reserver, the calendar dialogs, the site templates) has always
 *     rendered this way. The fallback locale is English, not French: `en` is BASE_LANG and
 *     an unknown `lang` must not silently produce "50,00 $" on an English page.
 *
 *   formatMoneyLedger(cents, currency, lang)    → "1,557.50 CAD" (en) / "1 557,50 CAD" (fr)
 *     The super-admin ledgers, where rows of several currencies sit in one column and are
 *     added up: the ISO code, never a symbol ("$" is CAD on one row and USD on the next,
 *     and fr-CA prints both as "$"), after a number grouped in the UI language so a
 *     platform-wide total stays readable.
 *
 * Both return the bare number when the currency is genuinely missing, and both uppercase
 * the code at render — every stored currency is lowercase ISO.
 */

const INTL_LOCALES = { en: "en-CA", es: "es-ES", fr: "fr-CA" };
const INTL_FALLBACK = "en-CA";

/** The `Intl` locale for a UI language — shared so the three lanes cannot drift. */
export const intlLocaleFor = (lang) => INTL_LOCALES[lang] || INTL_FALLBACK;

const code = (currency) => String(currency || "").trim().toUpperCase();

/**
 * "108.00 CAD". `decimals` defaults to 2; the programme cards print whole units (0).
 * A missing/non-finite amount renders as 0 rather than "NaN".
 */
export function formatMoneyCode(cents, currency, decimals = 2) {
  const n = Number(cents);
  const amount = (Number.isFinite(n) ? n / 100 : 0).toFixed(decimals);
  const c = code(currency);
  return c ? `${amount} ${c}` : amount;
}

/**
 * "$45.00" / "45,00 $" / "45,00 US$". Falls back to the code style when `Intl` refuses
 * the code (an unrecognised currency would otherwise throw and blank the whole card).
 */
export function formatMoneyIntl(cents, currency, lang) {
  const n = Number(cents);
  const amount = Number.isFinite(n) ? n / 100 : 0;
  const c = code(currency);
  if (!c) return amount.toFixed(2);
  try {
    return new Intl.NumberFormat(intlLocaleFor(lang), { style: "currency", currency: c }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${c}`;
  }
}

/**
 * "1,557.50 CAD" / "1 557,50 CAD" / "1557,50 CAD" (es groups from five digits). Same
 * missing-currency and non-finite rules as the other two styles.
 */
export function formatMoneyLedger(cents, currency, lang) {
  const amount = formatMoneyLedgerGroups(cents, lang).join("");
  const c = code(currency);
  return c ? `${amount} ${c}` : amount;
}

/**
 * `formatMoneyLedger`'s number (no currency), cut after each group separator:
 * 351676038925 → ["3 ", "516 ", "760 ", "389,25"] (fr), ["3,", "516,", "760,", "389.25"] (en).
 * Joined, the pieces are exactly that number. A layout that must hold any amount puts its line
 * break opportunities between these pieces, so a long figure can wrap without ever splitting a
 * digit group (`super_admin/LedgerCells.LedgerFigure`). The cut points are Intl's own, so they
 * follow each language's grouping (es leaves a four-digit number in one piece).
 */
export function formatMoneyLedgerGroups(cents, lang) {
  const n = Number(cents);
  const parts = new Intl.NumberFormat(intlLocaleFor(lang), { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    .formatToParts(Number.isFinite(n) ? n / 100 : 0);
  const groups = [""];
  parts.forEach((part) => {
    groups[groups.length - 1] += part.value;
    if (part.type === "group") groups.push("");
  });
  return groups;
}
